import { decodeImportFile } from './decodeImportFile';
import {
  type HeaderMatch,
  type ImportHeaderField,
  matchHeaders,
} from './matchHeaders';
import { papa } from './papaparse';
import { rewriteCsv } from './rewriteCsv';
import { IMPORT_COLUMN_SEPARATOR, sniffDelimiter } from './sniffDelimiter';

const CSV_TYPE = 'text/csv';

export type ImportFileRowProblem = 'quote' | 'tooManyValues';

/** Thrown for a row that cannot be written back without losing values. */
export class ImportFileRowError extends Error {
  /** Line of the file as a spreadsheet numbers it, the header being line 1. */
  readonly line: number;
  readonly problem: ImportFileRowProblem;

  constructor(line: number, problem: ImportFileRowProblem) {
    super(`Line ${line} cannot be read: ${problem}`);
    this.name = 'ImportFileRowError';
    this.line = line;
    this.problem = problem;
  }
}

/** Excel on Windows types a CSV file as a spreadsheet, which the import refuses. */
function asCsv(file: File): File {
  return file.type === CSV_TYPE
    ? file
    : new File([file], file.name, { type: CSV_TYPE });
}

function asUtf8Csv(text: string, file: File): File {
  return new File([text], file.name, { type: CSV_TYPE });
}

export type RepairedImportCsv = {
  file: File;
  /** Headers renamed to the field they were taken for. */
  changed: HeaderMatch['changed'];
};

/**
 * Brings an uploaded CSV file into the import format: decoded as
 * {@link decodeImportFile} does, columns split by the delimiter
 * {@link sniffDelimiter} picks, headers renamed to the fields
 * {@link matchHeaders} finds for them, written `§` separated as UTF-8 and
 * typed as CSV. A UTF-8 file that needs none of this, or whose delimiter
 * cannot be told, keeps its content as it is. In a rewritten file, rows
 * shorter than the header are filled with empty cells and rows without any
 * value are left out.
 *
 * @throws {ImportFileEncodingError} as {@link decodeImportFile} does.
 * @throws {ImportFileRowError} for a row with values beyond the header or a
 *   quote that does not open or close a value.
 */
export async function repairImportCsv(
  file: File,
  fields: readonly ImportHeaderField[],
  requiredHeaders: readonly string[],
): Promise<RepairedImportCsv> {
  const { encoding, text } = await decodeImportFile(file);
  const delimiter = sniffDelimiter(text, { fields, requiredHeaders });
  if (delimiter === undefined) {
    return {
      changed: [],
      file: encoding === 'utf-8' ? asCsv(file) : asUtf8Csv(text, file),
    };
  }

  const { data, errors } = papa.parse<string[]>(text, { delimiter });
  const quoteError = errors.at(0);
  if (quoteError) {
    throw new ImportFileRowError(quoteError.row + 1, 'quote');
  }
  const [header = [], ...rows] = data;
  rows.forEach((cells, index) => {
    if (cells.slice(header.length).some((cell) => cell.trim() !== '')) {
      throw new ImportFileRowError(index + 2, 'tooManyValues');
    }
  });

  const { changed, mapping } = matchHeaders(header, fields);
  const renamed = changed.filter(({ from, to }) => from.trim() !== to);
  if (
    encoding === 'utf-8' &&
    delimiter === IMPORT_COLUMN_SEPARATOR &&
    renamed.length === 0
  ) {
    return { changed: [], file: asCsv(file) };
  }

  const columns = header.map((column) => mapping[column] ?? column);
  const filledRows = rows.filter((cells) =>
    cells.some((cell) => cell.trim() !== ''),
  );

  return {
    changed: renamed,
    file: asUtf8Csv(rewriteCsv(columns, filledRows), file),
  };
}
